import type { ExecStatus, RunStatus as ApiRunStatus } from "@/api/runs";

// Unified status taxonomy spanning both run-level and exec-level
// statuses. "none" is the sentinel for IR nodes never visited in this
// run. The Workflow view holds it; the per-execution rows never do.
export type UnifiedStatus = ExecStatus | ApiRunStatus | "none";

export interface StatusClasses {
  bg: string;
  border: string;
  text: string;
  // BadgeVariant from the ui/Badge component, so callers don't have to
  // map twice. Kept narrow on purpose to avoid a circular import.
  badgeVariant: "neutral" | "info" | "warning" | "danger" | "success" | "accent";
  // Glyph used by IRNode pips and StatusBadge.
  glyph: string;
  label: string;
}

const RUNNING: StatusClasses = {
  bg: "bg-info-soft animate-pulse",
  border: "border-info",
  text: "text-fg-default",
  badgeVariant: "info",
  glyph: "▶",
  label: "Running",
};

// Border classes carry a light/dark split: the `-soft` fills are ~10%
// tints in the light palette, so at overview zoom the border is the only
// readable status signal — light mode gets the full-strength status color
// while dark keeps the original translucent border (already legible on
// dark surfaces, and softer glare next to the pulse animation).
const FINISHED: StatusClasses = {
  bg: "bg-success-soft",
  border: "border-success dark:border-success/60",
  text: "text-fg-default",
  badgeVariant: "success",
  glyph: "✓",
  label: "Finished",
};

const FAILED: StatusClasses = {
  bg: "bg-danger-soft",
  border: "border-danger dark:border-danger/60",
  text: "text-fg-default",
  badgeVariant: "danger",
  glyph: "✗",
  label: "Failed",
};

const FAILED_RESUMABLE: StatusClasses = {
  ...FAILED,
  label: "Failed (resumable)",
};

const PAUSED: StatusClasses = {
  bg: "bg-warning-soft",
  border: "border-warning dark:border-warning/60",
  text: "text-fg-default",
  badgeVariant: "warning",
  glyph: "⏸",
  label: "Paused (input)",
};

// PAUSED_OPERATOR sits between RUNNING and PAUSED visually: same
// pause glyph, but an info (cyan) palette to flag that no human form
// is required — the run is just frozen at an operator-initiated
// checkpoint. Keeps the canvas legible when a user pauses a run mid-
// flight to inspect or fork it.
const PAUSED_OPERATOR: StatusClasses = {
  bg: "bg-info-soft",
  border: "border-info dark:border-info/60",
  text: "text-fg-default",
  badgeVariant: "info",
  glyph: "⏸",
  label: "Paused (operator)",
};

// Neutral (unvisited/skipped/cancelled/queued) cards use border-strong in
// light mode: border-border-default is gray-200 on a near-white fill, invisible
// at overview zoom (the audit's "pale gray nodes on white").
const CANCELLED: StatusClasses = {
  bg: "bg-surface-2",
  border: "border-border-strong dark:border-border-default",
  text: "text-fg-muted",
  badgeVariant: "neutral",
  glyph: "⊘",
  label: "Cancelled",
};

const SKIPPED: StatusClasses = {
  bg: "bg-surface-2",
  border: "border-border-strong dark:border-border-default",
  text: "text-fg-subtle",
  badgeVariant: "neutral",
  glyph: "·",
  label: "Skipped",
};

const NONE: StatusClasses = {
  bg: "bg-surface-2 dark:bg-surface-1",
  border: "border-border-strong dark:border-border-default",
  text: "text-fg-subtle",
  badgeVariant: "neutral",
  glyph: "○",
  label: "Idle",
};

// Cloud-only state: run accepted by the server, sitting on the NATS
// queue, awaiting a runner pod. Visually distinct from PAUSED (no
// human action needed) and from RUNNING (no work happening yet) — a
// neutral hourglass keeps the list scannable. See cloud-ready plan
// §F (T-12).
const QUEUED: StatusClasses = {
  bg: "bg-surface-2",
  border: "border-border-strong dark:border-border-default",
  text: "text-fg-muted",
  badgeVariant: "neutral",
  glyph: "⧗",
  label: "Queued",
};

const TABLE: Record<UnifiedStatus, StatusClasses> = {
  running: RUNNING,
  finished: FINISHED,
  failed: FAILED,
  failed_resumable: FAILED_RESUMABLE,
  paused_waiting_human: PAUSED,
  paused_operator: PAUSED_OPERATOR,
  cancelled: CANCELLED,
  skipped: SKIPPED,
  queued: QUEUED,
  none: NONE,
};

export function statusClasses(status: UnifiedStatus): StatusClasses {
  return TABLE[status] ?? NONE;
}

// Backwards-compatible alias kept for the Workflow view, which has been
// passing a narrowed type. The implementation is the same lookup.
export type RunStatus = UnifiedStatus;
